import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import type { TabsProps } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useAddOnStep } from '@hooks/useAddOnStep';
import { IndividualChannelCard, ThemePackageCard } from '@components/ProductOffers/configuration/cards';
import { AddOnsStepFooter } from '@components/ProductOffers/configuration/steps/AddOnsStepFooter';
import { CloseButton, ErrorBanner } from '@components/ProductOffers/configuration/steps/shared.styles';
import {
  FilterBar,
  ScrollableContent,
} from '@components/ProductOffers/configuration/steps/TVAddOnsStep/TVAddOnsStep.styles';
import type { TVAddOnsStepProps } from '@components/ProductOffers/configuration/steps/TVAddOnsStep/TVAddOnsStep.types';
import { useAddOnTabs } from '@components/ProductOffers/configuration/steps/TVAddOnsStep/useAddOnTabs';
import TabPanel from '@shared/components/layout/TabPanel/TabPanel';
import SelectField from '@shared/components/inputs/SelectField/SelectField';
import { useConfigurationStore } from '@store/configurationStore';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { Theme } from '@mui/material/styles';

// MUI types `Tabs.variant` as a closed union, so declaration merging cannot add
// `addOnTabs` to it the way the other primitives allow (TS2717). The theme variant
// still matches — MUI resolves `variants[]` against `ownerState` — so the name is
// narrowed here rather than registered in an override interface that binds to nothing.
const ADD_ON_TABS_VARIANT = 'addOnTabs' as TabsProps['variant'];

export const TVAddOnsStep = ({ onClose, onSkip, onAddToPlan, confirmLabel, onCancel, loading }: TVAddOnsStepProps) => {
  const {
    showError,
    isSelected,
    handleToggle,
    validateAndSubmit,
    themePackages,
    individualChannels,
    allAddOns,
    themePackageCount,
    individualChannelCount,
  } = useAddOnStep();
  const editMode = useConfigurationStore((s) => s.editMode);
  const theme = useTheme();
  const {
    activeTab,
    handleTabChange,
    scrollRef,
    category,
    setCategory,
    categoryOptions,
    categorySelectWidth,
    filteredChannels,
  } = useAddOnTabs(themePackages, individualChannels);

  return (
    <>
      <DialogTitle>
        <Typography variant="title.step" className="StepHeader-title">
          {editMode ? (
            'Edit Your TV Add-ons'
          ) : (
            <>
              <span className="StepHeader-title-full">Enhance Your TV Plan With Optional Add-ons</span>
              <span className="StepHeader-title-compact">Add TV Add-ons</span>
            </>
          )}
        </Typography>
        <CloseButton onClick={onClose}>
          <CloseIcon variant="dialogCloseIcon" />
        </CloseButton>
      </DialogTitle>

      <DialogContent dividers={false} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack spacing={1} useFlexGap>
          <Typography variant="heading.subsection">Choose Your Add-Ons (Optional)</Typography>
          <Typography variant="body.md" color={theme.novus.borderBlack}>
            {`Your plan and equipment works with ${allAddOns.length} add-ons you might like.`}
          </Typography>
        </Stack>

        <Tabs value={activeTab} onChange={handleTabChange} variant={ADD_ON_TABS_VARIANT}>
          <Tab label="Theme Packages" variant="addOnTab" />
          <Tab label="Individual Channels" variant="addOnTab" />
        </Tabs>

        {showError && (
          <ErrorBanner>
            <ErrorOutlineIcon variant="errorBannerIcon" />
            <Typography variant="label.field" color={theme.novus.errorRed}>
              Selecting at least 1 add-on is required
            </Typography>
          </ErrorBanner>
        )}

        <FilterBar>
          <Typography variant="label.field" sx={{ lineHeight: '150%' }}>
            Filter By:
          </Typography>
          <Box sx={{ width: categorySelectWidth }}>
            <SelectField
              options={categoryOptions}
              value={category}
              onChange={(val) => setCategory(val ?? { label: 'Category', value: 'Category' })}
              placeholder="Category"
              inputHeight={40}
            />
          </Box>
        </FilterBar>

        <ScrollableContent ref={scrollRef}>
          <TabPanel value={activeTab} index={0}>
            <Box
              sx={(theme: Theme) => ({
                display: 'grid',
                p: '24px',
                gridTemplateColumns: theme.novus.themePackageGridColumns,
                // responsive form so the override beats the shared CardGrid base gap (sx is emitted before the styled base in the merged class)
                gap: { xs: theme.novus.themePackageGridGap },
              })}
            >
              {filteredChannels.map((pkg) => (
                <ThemePackageCard
                  key={pkg.code}
                  addOn={pkg}
                  isSelected={isSelected(pkg.code)}
                  onToggle={() => handleToggle(pkg)}
                />
              ))}
            </Box>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box
              sx={(theme: Theme) => ({
                display: 'grid',
                p: { xs: '20px', md: '24px' },
                gridTemplateColumns: theme.novus.individualChannelGridColumns,
                gap: { xs: theme.novus.individualChannelGridGap },
                justifyContent: 'center',
              })}
            >
              {filteredChannels.map((ch) => (
                <IndividualChannelCard
                  key={ch.code}
                  addOn={ch}
                  isSelected={isSelected(ch.code)}
                  onToggle={() => handleToggle(ch)}
                />
              ))}
            </Box>
          </TabPanel>
        </ScrollableContent>
      </DialogContent>

      <AddOnsStepFooter
        summary={
          <>
            <Box component="span" fontWeight={700}>
              {themePackageCount}
            </Box>
            {' Theme Packages, '}
            <Box component="span" fontWeight={700}>
              {individualChannelCount}
            </Box>
            {' Individual Channels'}
          </>
        }
        onSkip={onSkip}
        onSubmit={() => validateAndSubmit(onAddToPlan)}
        confirmLabel={confirmLabel}
        onCancel={onCancel}
        loading={loading}
      />
    </>
  );
};
